import {
  getWorkflowRun,
  type WorkflowRunWithResults,
} from '@/services/workflow/get-workflow-run';
import {
  spawnArtifactNodesForResult,
  getTerminalNodeIds,
} from '@/pages/workflow/helper/artifact-node-manager';
import type { CustomNode } from '@/types/node-types';
import type { NodeResult as NodeResultType } from '@/types/node-types';
import type { CustomEdge } from '@/types/edge-types';
import {
  type NodeStatusEvent,
  NodeEventType,
  NodeDataType,
  type NodeEvent,
  type TerminationEvent,
  TerminationStatusCode,
  WorkflowEventTypes,
} from '@/types/workflow-types';

export interface LoadRunDataResult {
  runData: WorkflowRunWithResults;
  preparedNodes: CustomNode[];
  edges: CustomEdge[];
  artifactNodes: CustomNode[];
  artifactEdges: CustomEdge[];
  synthesizedStatuses: Record<string, NodeStatusEvent[]>;
  displayNameById: Record<string, string>;
  nodeResultsByNodeId: Record<string, Record<string, unknown>>;
  isLiveRun: boolean;
}

export async function loadRunData(
  workflowId: string,
  runId: string,
  orgId: string,
): Promise<LoadRunDataResult> {
  const response = await getWorkflowRun(workflowId, runId, orgId);

  if (response.detail || !response.data) {
    throw new Error(response.detail || 'Failed to load run');
  }

  const runData = response.data;

  if (!runData.payload) {
    throw new Error('Run has no payload data');
  }

  const payload = runData.payload;
  const payloadNodes = (payload.nodes || []) as CustomNode[];
  const edges = (payload.edges || []) as CustomEdge[];

  // Coerce persisted dimensions/positions to numbers. Run payloads can
  // store `measured.width`/`height` as strings (e.g. "300"); React Flow's
  // getNodesBounds does `position.x + measured.width`, so a string turns
  // that into concatenation ("800" + "300" = "800300"), exploding the
  // bounding box and collapsing fitView to min-zoom off-screen. The saved-
  // workflow loader (workflow-store loadWorkflow) already does this.
  const preparedNodes = payloadNodes.map((node) => ({
    ...node,
    position: {
      ...node.position,
      x: Number(node.position?.x) || 0,
      y: Number(node.position?.y) || 0,
    },
    measured: node.measured
      ? {
          width: Number(node.measured.width) || 0,
          height: Number(node.measured.height) || 0,
        }
      : node.measured,
    selected: false,
  }));

  // Spawn artifact nodes for terminal nodes with file or markdown/text
  // display results.
  let artifactNodes: CustomNode[] = [];
  let artifactEdges: CustomEdge[] = [];
  const terminalIds = getTerminalNodeIds(preparedNodes, edges);
  if (runData.node_results && runData.node_results.length > 0) {
    runData.node_results.forEach((result) => {
      const matchingNode = preparedNodes.find(
        (node) => node.id === result.node_instance_id,
      );
      if (!matchingNode || !result.output_data) return;
      if (!terminalIds.has(matchingNode.id)) return;
      const { newNodes, newEdges } = spawnArtifactNodesForResult(
        matchingNode,
        result.output_data as NodeResultType | undefined,
        edges,
        [...preparedNodes, ...artifactNodes],
      );
      artifactNodes = [...artifactNodes, ...newNodes];
      artifactEdges = [...artifactEdges, ...newEdges];
    });
  }

  // Synthesise one terminal event per node_result row so the canvas
  // shows green-check / red-error badges in historical view.
  const synthesizedStatuses: Record<string, NodeStatusEvent[]> = {};
  const nodeResultsByNodeId: Record<string, Record<string, unknown>> = {};

  if (runData.node_results && runData.node_results.length > 0) {
    runData.node_results.forEach((result) => {
      const matchingNode = payloadNodes.find(
        (node) => node.id === result.node_instance_id,
      );
      if (!matchingNode) return;

      if (result.status === 'skipped') {
        // Persist only durable control metadata. A skipped node has no
        // fabricated output value, but the canvas still needs its terminal
        // status (and backend-provided reason, when present) after reload.
        const legacyReason =
          result.output_data && typeof result.output_data.reason === 'string'
            ? result.output_data.reason
            : null;
        nodeResultsByNodeId[matchingNode.id] = {
          status: 'skipped',
          ...(result.reason || legacyReason
            ? { reason: result.reason ?? legacyReason }
            : {}),
        };
      } else if (result.output_data) {
        nodeResultsByNodeId[matchingNode.id] = result.output_data;
      }

      let eventType: NodeEventType | null = null;
      if (result.status === 'completed') {
        eventType = NodeEventType.EXECUTION_SUCCESSFUL;
      } else if (result.status === 'failed') {
        eventType = NodeEventType.EXECUTION_FAILED;
      }
      if (eventType === null) return;

      const synthEvent = {
        event_type: eventType,
        display_type: NodeDataType.NODE_RESULT,
        node_id: matchingNode.id,
        // Required for the events panel: without it the row fails the
        // ``isNodeEvent`` guard and its group title falls back to a generic
        // "Result" (notably for nodes with only a terminal event, no CoT).
        node_display_name:
          matchingNode.data?.struct?.user_config_name ??
          matchingNode.data?.struct?.display_name ??
          '',
        timestamp:
          result.completed_at ||
          result.started_at ||
          result.created_at ||
          new Date().toISOString(),
        data: result.output_data ?? null,
        error: result.error_message ?? null,
      } as unknown as NodeEvent;

      synthesizedStatuses[matchingNode.id] = [synthEvent];
    });
  }

  // Reflect the run's terminal status on the canvas pill. Without this the
  // pill is derived purely from node_results — which are empty for a run
  // stopped before any node persisted — so classifyTerminalRun defaults to
  // "Run complete", masking a stopped/failed run on reload.
  const runStatus = runData.status?.toLowerCase();
  if (runStatus === 'stopped' || runStatus === 'failed') {
    const terminalEvent: TerminationEvent = {
      event_type: WorkflowEventTypes.TERMINATION as 'termination',
      event_id: `synth:terminal:${runData.session_id}`,
      timestamp:
        runData.completed_at || runData.updated_at || new Date().toISOString(),
      status_code:
        runStatus === 'stopped'
          ? TerminationStatusCode.STOPPED
          : TerminationStatusCode.ERROR,
    };
    synthesizedStatuses['workflow_events'] = [terminalEvent];
  }

  const displayNameById: Record<string, string> = {};
  for (const node of payloadNodes) {
    displayNameById[node.id] =
      node.data?.struct?.user_config_name ??
      node.data?.struct?.display_name ??
      '';
  }

  const isLiveRun = runData.status === 'running' || runData.status === 'paused';

  return {
    runData,
    preparedNodes,
    edges,
    artifactNodes,
    artifactEdges,
    synthesizedStatuses,
    displayNameById,
    nodeResultsByNodeId,
    isLiveRun,
  };
}

export interface ApplyRunToCanvasDeps {
  setNodes: (nodes: CustomNode[]) => void;
  setEdges: (edges: CustomEdge[]) => void;
  updateNodeResult: (nodeId: string, data: Record<string, unknown>) => void;
  setLoadedNodeStatuses: (
    statuses: Record<string, NodeStatusEvent[]> | null,
  ) => void;
  setCurrentSession: (sessionId: string) => void;
  setLastExecutionTimestamp: (timestamp: string | null) => void;
  setIsHistoricalData: (isHistorical: boolean) => void;
}

export function applyRunToCanvas(
  result: LoadRunDataResult,
  deps: ApplyRunToCanvasDeps,
): void {
  const allNodes =
    result.artifactNodes.length > 0
      ? [...result.preparedNodes, ...result.artifactNodes]
      : result.preparedNodes;
  const allEdges =
    result.artifactEdges.length > 0
      ? [...result.edges, ...result.artifactEdges]
      : result.edges;

  deps.setNodes(allNodes);
  deps.setEdges(allEdges);

  for (const [nodeId, data] of Object.entries(result.nodeResultsByNodeId)) {
    deps.updateNodeResult(nodeId, data);
  }

  deps.setLoadedNodeStatuses(result.synthesizedStatuses);
  deps.setCurrentSession(result.runData.session_id);
  deps.setLastExecutionTimestamp(
    result.runData.completed_at || result.runData.created_at,
  );

  if (!result.isLiveRun) {
    deps.setIsHistoricalData(true);
  }
}
